import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { dashboardAPI, seedAPI, landsAPI, projectsAPI, buildersAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Map,
  FolderKanban,
  Users,
  TrendingUp,
  Plus,
  ArrowRight,
  CheckCircle2,
  Clock,
  AlertCircle,
  Building2,
  Package,
  Star,
  Loader2,
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentLands, setRecentLands] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [topBuilders, setTopBuilders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Seed data first (for demo purposes)
      try {
        await seedAPI.seed();
      } catch (e) {
        // Ignore if already seeded
      }

      // Load stats
      const statsRes = await dashboardAPI.getStats();
      setStats(statsRes.data);

      // Load based on role
      if (user?.role === 'landowner') {
        const [landsRes, projectsRes] = await Promise.all([
          landsAPI.getAll(),
          projectsAPI.getAll(),
        ]);
        setRecentLands(landsRes.data.slice(0, 3));
        setRecentProjects(projectsRes.data.slice(0, 3));
      }

      // Load top builders for all users
      const buildersRes = await buildersAPI.getAll({ limit: 5, verified_only: true });
      setTopBuilders(buildersRes.data.builders || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const renderLandownerStats = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <Card className="border border-border hover:border-secondary/50 transition-colors" data-testid="stats-lands">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">My Lands</p>
              <p className="text-3xl font-heading font-bold mt-1">{stats?.lands_count || 0}</p>
            </div>
            <div className="w-12 h-12 bg-[#0F2640] rounded-sm flex items-center justify-center">
              <Map className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border hover:border-secondary/50 transition-colors" data-testid="stats-projects">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Active Projects</p>
              <p className="text-3xl font-heading font-bold mt-1">{stats?.projects_count || 0}</p>
            </div>
            <div className="w-12 h-12 bg-[#10B981] rounded-sm flex items-center justify-center">
              <FolderKanban className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border hover:border-secondary/50 transition-colors" data-testid="stats-stages">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Active Stages</p>
              <p className="text-3xl font-heading font-bold mt-1">{stats?.active_stages || 0}</p>
            </div>
            <div className="w-12 h-12 bg-[#F59E0B] rounded-sm flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border hover:border-secondary/50 transition-colors" data-testid="stats-investment">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Total Investment</p>
              <p className="text-3xl font-heading font-bold mt-1">
                BWP {(stats?.total_investment || 0).toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-[#0F2640] rounded-sm flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderBuilderStats = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <Card className="border border-border" data-testid="stats-builder-projects">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Total Projects</p>
              <p className="text-3xl font-heading font-bold mt-1">{stats?.total_projects || 0}</p>
            </div>
            <div className="w-12 h-12 bg-[#0F2640] rounded-sm flex items-center justify-center">
              <FolderKanban className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border" data-testid="stats-rating">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Rating</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-3xl font-heading font-bold">{(stats?.rating || 0).toFixed(1)}</p>
                <Star className="w-6 h-6 text-amber-400 fill-current" />
              </div>
            </div>
            <div className="w-12 h-12 bg-[#F59E0B] rounded-sm flex items-center justify-center">
              <Star className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border" data-testid="stats-verified">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Status</p>
              <p className="text-lg font-heading font-bold mt-1">
                {stats?.verified ? (
                  <span className="text-[#10B981] flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" /> Verified
                  </span>
                ) : (
                  <span className="text-amber-500 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" /> Pending
                  </span>
                )}
              </p>
            </div>
            <div className="w-12 h-12 bg-[#10B981] rounded-sm flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border" data-testid="stats-availability">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Availability</p>
              <p className="text-lg font-heading font-bold mt-1">
                {stats?.availability ? (
                  <span className="text-[#10B981]">Available</span>
                ) : (
                  <span className="text-muted-foreground">Busy</span>
                )}
              </p>
            </div>
            <div className="w-12 h-12 bg-[#0F2640] rounded-sm flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSupplierStats = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <Card className="border border-border" data-testid="stats-materials">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Materials Listed</p>
              <p className="text-3xl font-heading font-bold mt-1">{stats?.materials_count || 0}</p>
            </div>
            <div className="w-12 h-12 bg-[#0F2640] rounded-sm flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border" data-testid="stats-orders">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Total Orders</p>
              <p className="text-3xl font-heading font-bold mt-1">{stats?.total_orders || 0}</p>
            </div>
            <div className="w-12 h-12 bg-[#10B981] rounded-sm flex items-center justify-center">
              <FolderKanban className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border" data-testid="stats-supplier-rating">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Rating</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-3xl font-heading font-bold">{(stats?.rating || 0).toFixed(1)}</p>
                <Star className="w-6 h-6 text-amber-400 fill-current" />
              </div>
            </div>
            <div className="w-12 h-12 bg-[#F59E0B] rounded-sm flex items-center justify-center">
              <Star className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border" data-testid="stats-supplier-verified">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Status</p>
              <p className="text-lg font-heading font-bold mt-1">
                {stats?.verified ? (
                  <span className="text-[#10B981] flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" /> Verified
                  </span>
                ) : (
                  <span className="text-amber-500 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" /> Pending
                  </span>
                )}
              </p>
            </div>
            <div className="w-12 h-12 bg-[#10B981] rounded-sm flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">
              {getGreeting()}, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">
              Here's what's happening with your {user?.role === 'landowner' ? 'lands and projects' : 'account'}
            </p>
          </div>
          {user?.role === 'landowner' && (
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/lands')} data-testid="add-land-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Land
              </Button>
              <Button onClick={() => navigate('/projects')} data-testid="new-project-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        {user?.role === 'landowner' && renderLandownerStats()}
        {user?.role === 'builder' && renderBuilderStats()}
        {user?.role === 'supplier' && renderSupplierStats()}

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Lands (Landowner only) */}
          {user?.role === 'landowner' && (
            <Card className="border border-border lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="font-heading">My Lands</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/lands')}>
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {recentLands.length > 0 ? (
                  <div className="space-y-4">
                    {recentLands.map((land) => (
                      <div
                        key={land.land_id}
                        className="flex items-center justify-between p-4 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => navigate('/lands')}
                        data-testid={`land-item-${land.land_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-[#0F2640] rounded-sm flex items-center justify-center">
                            <Map className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium">{land.title}</p>
                            <p className="text-sm text-muted-foreground">
                              {land.city} • {land.size_sqm.toLocaleString()} sqm
                            </p>
                          </div>
                        </div>
                        <span className="text-xs font-medium uppercase text-muted-foreground">
                          {land.land_type}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Map className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No lands registered yet</p>
                    <Button onClick={() => navigate('/lands')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Register Your First Land
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Top Builders */}
          <Card className={`border border-border ${user?.role !== 'landowner' ? 'lg:col-span-2' : ''}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="font-heading">Top Builders</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/builders')}>
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {topBuilders.length > 0 ? (
                <div className="space-y-4">
                  {topBuilders.slice(0, 5).map((builder) => (
                    <div
                      key={builder.builder_id}
                      className="flex items-center justify-between"
                      data-testid={`builder-item-${builder.builder_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">{builder.user?.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {builder.skills?.slice(0, 2).join(', ')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-amber-400 fill-current" />
                          <span className="font-medium">{builder.rating?.toFixed(1)}</span>
                        </div>
                        {builder.verified && (
                          <span className="text-xs text-[#10B981]">Verified</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  No builders found
                </p>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions for Landowners */}
          {user?.role === 'landowner' && (
            <Card className="border border-border">
              <CardHeader className="pb-4">
                <CardTitle className="font-heading">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/valuation')}
                  data-testid="quick-valuation"
                >
                  <Building2 className="w-4 h-4 mr-3" />
                  Get Land Valuation
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/builders')}
                  data-testid="quick-builders"
                >
                  <Users className="w-4 h-4 mr-3" />
                  Find Builders
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/suppliers')}
                  data-testid="quick-suppliers"
                >
                  <Package className="w-4 h-4 mr-3" />
                  Browse Materials
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
